/**
 * useDeveloperAccess - Hook for managing developer access
 *
 * Fetches developer access list from Firestore and checks if current user has access.
 * Uses React Query for caching with 5-minute stale time.
 *
 * @module hooks/useDeveloperAccess
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { db, doc, getDoc, setDoc, updateDoc, arrayUnion, arrayRemove, serverTimestamp } from '@/lib/firestoreClient';
import { useAuth } from '@/auth/AuthContext';

// ============================================
// Constants
// ============================================

/**
 * Firestore document path for developer access configuration
 */
const DEVELOPER_ACCESS_PATH = 'config/developerAccess';

/**
 * Default seed emails for initial setup
 * These are added automatically if no developer access config exists
 */
const SEED_DEVELOPER_EMAILS = [
    'khalid@etchare.com',
    'khalid.moukhtar@gmail.com',
];

// ============================================
// Types
// ============================================

interface DeveloperAccessConfig {
    emails: string[];
    updatedAt?: unknown;
    updatedBy?: string;
}

// ============================================
// Query Keys
// ============================================

export const developerAccessQueryKeys = {
    all: ['developerAccess'] as const,
    config: () => [...developerAccessQueryKeys.all, 'config'] as const,
};

// ============================================
// Fetch Function
// ============================================

async function fetchDeveloperAccess(): Promise<DeveloperAccessConfig> {
    try {
        const docRef = doc(db, DEVELOPER_ACCESS_PATH);
        const docSnap = await getDoc(docRef);

        if (docSnap.exists()) {
            return docSnap.data() as DeveloperAccessConfig;
        }

        // Return empty config instead of auto-seeding
        // Auto-seeding requires Admin permissions and should be done via script
        // Return seed emails instead of empty list so default admins have access
        return { emails: SEED_DEVELOPER_EMAILS };
    } catch {
        // Silently fallback to seed emails — Firestore rules deny this read for
        // non-developer users, which is expected. Do NOT log to console as it
        // exposes the config/developerAccess path to anyone inspecting DevTools.
        return { emails: SEED_DEVELOPER_EMAILS };
    }
}

// ============================================
// Hooks
// ============================================

/**
 * Hook to check if current user has developer access
 *
 * @returns Object with isDeveloper flag, loading state, and email list
 *
 * @example
 * ```tsx
 * const { isDeveloper, isLoading } = useDeveloperAccess();
 * if (isDeveloper) {
 *   // Show developer tools
 * }
 * ```
 */
export function useDeveloperAccess() {
    const { user } = useAuth();

    const { data, isLoading, error } = useQuery({
        queryKey: developerAccessQueryKeys.config(),
        queryFn: fetchDeveloperAccess,
        staleTime: 5 * 60 * 1000, // 5 minutes
        gcTime: 30 * 60 * 1000, // 30 minutes
    });

    // Seed emails are always developers regardless of Firestore state.
    // This ensures both founder accounts always have access even if the
    // config/developerAccess doc is missing or misconfigured.
    const isDeveloper = Boolean(
        user?.email && (
            SEED_DEVELOPER_EMAILS.includes(user.email) ||
            data?.emails?.includes(user.email)
        )
    );

    return {
        isDeveloper,
        isLoading,
        emails: data?.emails ?? [],
        error,
    };
}

/**
 * Hook to manage developer access (add/remove emails)
 *
 * @returns Mutation functions for updating developer emails
 *
 * @example
 * ```tsx
 * const { addEmail, removeEmail, isUpdating } = useDeveloperAccessMutations();
 * addEmail('new@example.com');
 * ```
 */
export function useDeveloperAccessMutations() {
    const queryClient = useQueryClient();
    const { user } = useAuth();

    const addEmailMutation = useMutation({
        mutationFn: async (email: string) => {
            if (!user) {
                throw new Error('Must be authenticated to update developer access');
            }

            const docRef = doc(db, DEVELOPER_ACCESS_PATH);

            // Try to update existing document first (atomic add)
            try {
                await updateDoc(docRef, {
                    emails: arrayUnion(email),
                    updatedAt: serverTimestamp(),
                    updatedBy: user.uid,
                });
            } catch (_err) {
                // If document does not exist, create it (fallback)
                // This covers the edge case where initial seeding hasn't happened
                await setDoc(docRef, {
                    emails: [email],
                    updatedAt: serverTimestamp(),
                    updatedBy: user.uid,
                }, { merge: true });
            }

            return email;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: developerAccessQueryKeys.config() });
        },
    });

    const removeEmailMutation = useMutation({
        mutationFn: async (email: string) => {
            if (!user) {
                throw new Error('Must be authenticated to update developer access');
            }

            const docRef = doc(db, DEVELOPER_ACCESS_PATH);

            await updateDoc(docRef, {
                emails: arrayRemove(email),
                updatedAt: serverTimestamp(),
                updatedBy: user.uid,
            });

            return email;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: developerAccessQueryKeys.config() });
        },
    });

    const addEmail = async (email: string) => {
        const current = queryClient.getQueryData<DeveloperAccessConfig>(
            developerAccessQueryKeys.config()
        );
        const currentEmails = current?.emails ?? [];

        if (currentEmails.includes(email)) {
            throw new Error('Email already has developer access');
        }

        return addEmailMutation.mutateAsync(email);
    };

    const removeEmail = async (email: string) => {
        const current = queryClient.getQueryData<DeveloperAccessConfig>(
            developerAccessQueryKeys.config()
        );
        const currentEmails = current?.emails ?? [];

        if (!currentEmails.includes(email)) {
            throw new Error('Email does not have developer access');
        }

        return removeEmailMutation.mutateAsync(email);
    };

    return {
        addEmail,
        removeEmail,
        isUpdating: addEmailMutation.isPending || removeEmailMutation.isPending,
        error: addEmailMutation.error || removeEmailMutation.error,
    };
}
